"use client";

import * as Sentry from "@sentry/nextjs";
// biome-ignore lint/style/useNodejsImportProtocol: "node:crypto" not working with Vercel
import crypto from "crypto";
import { useParams } from "next/navigation";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { ulid } from "ulid";
import { v4 as uuid } from "uuid";
import { useGraphql } from "~/api-client";
import { parseMutationErrorMessage } from "~/graphql-client";
import {
  type CartItemChoiceInput,
  type CartItemInput,
  type CartItemOptionInput,
  CartItemType,
  type CreateCartMutation,
  FulfillmentType,
} from "~/graphql-client/generated";
import type {
  Cart,
  CartItem,
  CartItemChoiceRecord,
  CartItemOption,
  CartItemOptionRecord,
  CartItemRecord,
  CartRecord,
  CheckoutDeliveryAddress,
  ItemSource,
  OrderDispatchOption,
  Product,
  TimeSlot,
  UpsellRequest,
  User,
} from "~/types";
import {
  addCartItemAnalyticsEvent,
  viewItemAnalyticsEvent,
} from "~/utils/analytics";
import { isCartItemDealComplete } from "~/utils/cart";
import {
  checkExclusiveDeals,
  getConflictingDeals,
  isLegacyDealExclusive,
  type SharedDealFields,
} from "~/utils/deal";
import {
  poolExistingItemsIntoDeal,
  poolPlainItemIntoDeals,
} from "~/utils/deal-pooling";
import { toDeliveryAddress } from "~/utils/delivery-address";
import { getGuestDetailsClient } from "~/utils/guest-details";
import { getClientAuthToken } from "~/utils/token";
import useCurrency from "./currency";
import {
  dealToPoolingProduct,
  useLegacyDeals,
  useLocationDeals,
} from "./deals";
import { useIsFeatureEnabled } from "./features";
import { useLocationOrderingEnabled } from "./locations";
import { toEnumFulfillmentType } from "./menu-v2";
import { useUser } from "./user";

const initialState: State = {
  carts: new Map<string, Cart>(),
  dispatchOption: null,
};

// Joins deal titles into a readable list: "A", "A and B", or "A, B and C".
function joinDealTitles(deals: SharedDealFields[]): string {
  const names = deals.map((d) => d.title);
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

// Builds the CART_BAD reason for an invalid exclusive-deal combination, tailored to
// whether the exclusive deal(s) are mixed with other deals or just over the one-deal limit.
function exclusiveDealErrorReason(
  exclusiveDeals: SharedDealFields[],
  otherDeals: SharedDealFields[],
): string {
  const exclusiveNames = joinDealTitles(exclusiveDeals);

  // An exclusive deal can't share the cart with any other deal.
  if (otherDeals.length > 0) {
    const exclusiveLabel =
      exclusiveDeals.length > 1 ? "exclusive deals" : "an exclusive deal";
    return `Exclusive deals can't be combined with other deals. Your cart has ${exclusiveLabel} (${exclusiveNames}) alongside ${joinDealTitles(otherDeals)}. Remove ${exclusiveNames}, or remove your other deals to keep just the exclusive deal.`;
  }

  // Only exclusive deals in the cart, but more than one unit (distinct deals, or the same
  // exclusive deal added more than once).
  const toRemove =
    exclusiveDeals.length > 1
      ? `all but one of ${exclusiveNames}`
      : `the extra ${exclusiveNames}`;
  return `You can only have one exclusive deal in your cart. Remove ${toRemove} to continue.`;
}

export function CartsProvider(props: {
  onCartChangeHook?: (cart: Cart, cartCustomer: CartCustomer) => Cart;
  children: React.ReactNode;
}) {
  const [state, dispatch] = useReducer(reducer, initialState, (state) => {
    try {
      const prev =
        typeof localStorage !== "undefined" &&
        localStorage.getItem("carts_2.0");

      if (prev) {
        const persistedState: PersistedState = JSON.parse(prev);
        const map = new Map<string, Cart>(Object.entries(persistedState.carts));

        return {
          dispatchOption: persistedState.dispatchOption,
          timeSlot: persistedState.timeSlot,
          serviceRef: persistedState.serviceRef,
          carts: map,
          deliveryAddress: persistedState.deliveryAddress,
        };
      }
      return state;
    } catch (err) {
      const error = err as unknown as Error;
      if (error.message !== "localStorage is not defined")
        console.error(error.message);
      return state;
    }
  });

  const params = useParams();

  useEffect(() => {
    // push what we have to storage after state updates
    const persistedState: PersistedState = {
      dispatchOption: state.dispatchOption,
      timeSlot: state.timeSlot,
      carts: Object.fromEntries(state.carts.entries()),
      deliveryAddress: state.deliveryAddress,
      serviceRef: state.serviceRef,
    };

    if (typeof localStorage !== "undefined") {
      // save
      localStorage.setItem("carts_2.0", JSON.stringify(persistedState));

      // remove the old redux state
      localStorage.removeItem("user_state");

      // remove the old cart start (for a location)
      if (params.location) {
        localStorage.removeItem(`cart_${params.location}`);
      }
    }
  }, [params.location, state]);

  return (
    <cartsCtx.Provider
      value={{
        carts: state.carts,
        deliveryAddress: state.deliveryAddress || null,
        dispatchOption: state.dispatchOption || null,
        timeSlot: state.timeSlot || null,
        serviceRef: state.serviceRef || null,
        dispatch,
        onCartChangeHook: props.onCartChangeHook,
      }}
    >
      {props.children}
    </cartsCtx.Provider>
  );
}

const EMPTY_CART: Cart = {
  items: [],
  id: ulid(),
  checkingCart: false,
};

interface State {
  carts: Carts;
  deliveryAddress?: CheckoutDeliveryAddress | null;
  dispatchOption?: OrderDispatchOption | null;
  timeSlot?: TimeSlot | null;
  serviceRef?: string | null;
}

interface PersistedState {
  carts: {
    [k: string]: Cart;
  };
  deliveryAddress?: CheckoutDeliveryAddress | null;
  dispatchOption?: OrderDispatchOption | null;
  timeSlot?: TimeSlot | null;
  serviceRef?: string | null;
}

export function useCart(cartLocationRef: string | null): CartService {
  // Used for analytics, non-blocking hooks.
  const currency = useCurrency();
  const apartmentMode = currency === "GBP";
  const { getMenus, createCart, submitUpsellSuggestion, getLocationDeals } =
    useGraphql();
  const { user, isGuest } = useUser();

  const {
    carts,
    deliveryAddress,
    dispatch,
    dispatchOption,
    serviceRef,
    timeSlot,
    onCartChangeHook,
  } = useContext(cartsCtx);

  const { orderingEnabled } = useLocationOrderingEnabled(
    // orderingEnabled only required if dispatchOption not set
    !dispatchOption && cartLocationRef ? cartLocationRef : undefined,
  );

  const newUpsells = useIsFeatureEnabled(
    "web.intelligent-upsells",
    cartLocationRef || undefined,
  );

  // Deals are needed to enforce the "max 1 exclusive deal per cart" rule in assertCartValidity and addItem.
  const { deals } = useLocationDeals(
    cartLocationRef ?? undefined,
    dispatchOption,
  );

  // Legacy deals are needed to enforce the "max 1 exclusive deal per cart" rule in assertCartValidity and addItem.
  const { legacyDeals } = useLegacyDeals(dispatchOption, timeSlot);

  // We use this order to fallback if the fulfillment type is not set
  const fulfillmentTypes = useMemo(() => {
    const fulfillmentTypesArr: OrderDispatchOption[] = [];
    if (orderingEnabled?.orderAheadEnabled) fulfillmentTypesArr.push("PICK_UP");
    if (orderingEnabled?.orderDeliveryEnabled)
      fulfillmentTypesArr.push("DELIVERY");
    if (orderingEnabled?.orderAtTableEnabled)
      fulfillmentTypesArr.push("SERVICE");
    return fulfillmentTypesArr;
  }, [orderingEnabled]);

  const currentCart = !cartLocationRef
    ? EMPTY_CART
    : carts.get(cartLocationRef) || EMPTY_CART;

  useEffect(() => {
    if (!fulfillmentTypes.length || dispatchOption) return;

    dispatch({
      type: "SET_OPTIONS",
      dispatchOption: fulfillmentTypes[0],
      timeSlot,
      serviceRef,
      cart: currentCart,
      locationRef: cartLocationRef,
    });
  }, [
    dispatch,
    fulfillmentTypes,
    timeSlot,
    serviceRef,
    currentCart,
    cartLocationRef,
    dispatchOption,
  ]);

  const cloneCart = (
    cart: Cart,
    patch: Partial<Cart> | undefined = {},
  ): Cart => {
    const { hash } = getCartCustomer(user, isGuest);

    return {
      ...cart,
      ...patch,
      id: ulid(),
      checkingCart: false,
      status: undefined,
      invalidReason: undefined,
      hash,
      updatedAt: new Date(),
    };
  };

  const submitUpsellForCart = async (locationRef: string, cart: Cart) => {
    if (!newUpsells || !locationRef || !cart.items.length || !dispatchOption) {
      return;
    }

    try {
      const result = await submitUpsellSuggestion({
        location: locationRef,
        // Don't send 'isDeal' to upsell
        cart: { items: cart.items.map(stripIsDealForUpsells) },
        // This should never hit the right hand side, since we check if dispatchOption is falsey already
        fulfillmentType:
          toEnumFulfillmentType(dispatchOption) || FulfillmentType.PickUp,
      });

      if (
        result.submitUpsellSuggestion.requestId &&
        result.submitUpsellSuggestion.provider &&
        !result.submitUpsellSuggestion.error
      ) {
        dispatch({
          type: "UPDATE_CART_UPSELL",
          locationRef,
          cartId: cart.id,
          upsellRequest: {
            requestId: result.submitUpsellSuggestion.requestId,
            provider: result.submitUpsellSuggestion.provider,
          },
        });
      }
    } catch (error) {
      // We don't want to surface anything to the user the upsellRequestId will either be stale (acceptable) or missing,
      // in which case it falls back to legacy
      console.error("Failed to submit upsell suggestion:", error);
      Sentry.captureException(error, {
        extra: { locationRef },
        tags: { source: "upsells" },
        level: "error",
      });
    }
  };

  // Resolve a deal cart item to a lightweight product (deal definition only, no
  // menu) for pooling — enough for slot-fit and completeness checks.
  const resolveDealProduct = (dealItem: CartItem): Product | undefined => {
    const deal = deals.find((d) => d.id === dealItem.productId);
    return deal ? dealToPoolingProduct(deal) : undefined;
  };

  return {
    cart: currentCart,
    dispatchOption: dispatchOption || null,
    deliveryAddress: deliveryAddress || null,
    timeSlot: timeSlot || null,
    serviceRef: serviceRef || null,
    upsellRequest: currentCart.upsellRequest || null,
    setOrderingOptions: (
      dispatchOption: OrderDispatchOption,
      timeSlot: TimeSlot | null,
      serviceRef: string | null,
    ) => {
      const cart = cloneCart(currentCart);
      const locationRef = cartLocationRef;

      dispatch({
        type: "SET_OPTIONS",
        dispatchOption,
        timeSlot,
        serviceRef,
        cart,
        locationRef,
      });
    },
    addItem: (locationRef: string, item: CartItem, list?: string) => {
      // Resolve the deal being added from the loaded, availability-filtered deals
      // list; it's needed below to drop conflicts and enforce the
      // one-exclusive-deal rule.
      const newDeal = item.isDeal
        ? deals.find((d) => d.id === item.productId)
        : undefined;

      // Resolve the deal being added from legacy deals (i.e. product in the Hot Deals menu category) if it exists
      const legacyDeal = legacyDeals.find((d) => d.id === item.productId);

      const isDeal = item.isDeal || Boolean(legacyDeal);

      // If a deal can't be resolved — deals still loading (empty list), or the
      // deal isn't currently available — block the add rather than mutate the
      // cart with exclusive-deal enforcement silently skipped. The deal UI is
      // skeleton-gated on the same loading state, so in practice this is a safety
      // net; non-deal adds are unaffected.
      if (item.isDeal && !newDeal) return;

      // Magnet: a plain item being added is pooled into the oldest incomplete
      // deal with an open matching slot, instead of becoming its own line. If
      // nothing fits, it falls through to a normal append below.
      if (!isDeal) {
        const pooled = poolPlainItemIntoDeals(
          currentCart.items,
          item,
          resolveDealProduct,
        );
        if (pooled.pooled) {
          addCartItemAnalyticsEvent(
            currency,
            locationRef,
            [item],
            getMenus,
            getLocationDeals,
            dispatchOption,
          );
          const cart = cloneCart(currentCart, {
            items: pooled.items,
            inFlightItem: undefined,
          });
          dispatch({ type: "SET_CART", cart, locationRef });
          submitUpsellForCart(locationRef, cart);
          return;
        }
      }

      addCartItemAnalyticsEvent(
        currency,
        locationRef,
        [item],
        getMenus,
        getLocationDeals,
        dispatchOption,
      );

      let tags: string[] | undefined;
      if (currentCart.tags || list) {
        tags = Array.from(currentCart.tags ?? []);
        if (list) tags.push(`loke:upsell=${list}:${item.productId}`);
      }

      // Drop whatever the new deal conflicts with before appending it: an
      // exclusive deal can't coexist with any other deal, and a non-exclusive
      // deal can't coexist with exclusives.
      const conflictingLineItemIds = isDeal
        ? new Set(
            getConflictingDeals(
              currentCart.items,
              deals,
              legacyDeals,
              newDeal?.exclusive ??
                (legacyDeal ? isLegacyDealExclusive(legacyDeal) : false),
            ).lineItems.map((it) => it.lineItemId),
          )
        : null;
      const existingItemsWithoutConflicts = conflictingLineItemIds
        ? currentCart.items.filter(
            (cartItem) => !conflictingLineItemIds.has(cartItem.lineItemId),
          )
        : currentCart.items;

      // A plain item identical to an existing line merges into it (2x + 1x = 3x);
      // deals always append as their own line.
      const items = isDeal
        ? [...existingItemsWithoutConflicts, item]
        : addOrMergePlainItem(existingItemsWithoutConflicts, item);

      const cart = cloneCart(currentCart, {
        tags,
        items,
        inFlightItem: undefined,
      });

      dispatch({ type: "SET_CART", cart, locationRef });
      submitUpsellForCart(locationRef, cart);
    },
    // Code-entry add: append a deal (usually incomplete) and pool any matching
    // plain items already in the cart into it, in one dispatch. `dealProduct` is
    // the menu-resolved product (from the caller, which has the menu) so default
    // choices and slot-fitting resolve against real products.
    addDealFromCode: (
      locationRef: string,
      dealItem: CartItem,
      dealProduct: Product,
    ) => {
      const newDeal = deals.find((d) => d.id === dealItem.productId);
      // Block if the deal can't be resolved (deals still loading / unavailable),
      // mirroring the addItem guard.
      if (!newDeal) return;

      // Match against the deal definition (productDef ids) rather than the
      // menu-resolved product: the latter comes from useBaseMenu, whose top-level
      // lookup can't resolve some choices (e.g. drinks) and yields unmatchable
      // placeholders. The menu-resolved product is still used for defaults below.
      const poolingProduct = dealToPoolingProduct(newDeal);
      const { remainingItems, dealItem: pooledDeal } =
        poolExistingItemsIntoDeal(currentCart.items, dealItem, poolingProduct);

      // Apply the deal's defaults only to slots the pooled items didn't fill,
      // so the customer's own items take precedence over defaults.
      const filledDeal = fillDealDefaults(pooledDeal, dealProduct);

      addCartItemAnalyticsEvent(
        currency,
        locationRef,
        [filledDeal],
        getMenus,
        getLocationDeals,
        dispatchOption,
      );

      // Drop deals the new deal conflicts with (exclusive-deal rule), same as addItem.
      const conflictingLineItemIds = new Set(
        getConflictingDeals(
          remainingItems,
          deals,
          legacyDeals,
          newDeal.exclusive,
        ).lineItems.map((it) => it.lineItemId),
      );
      const itemsWithoutConflicts = remainingItems.filter(
        (it) => !conflictingLineItemIds.has(it.lineItemId),
      );

      const cart = cloneCart(currentCart, {
        items: [...itemsWithoutConflicts, filledDeal],
        inFlightItem: undefined,
      });

      dispatch({ type: "SET_CART", cart, locationRef });
      submitUpsellForCart(locationRef, cart);
    },
    removeItem: (locationRef: string, lineId: string) => {
      const removedIndex = currentCart.items.findIndex(
        (it) => it.lineItemId === lineId,
      );
      const removed =
        removedIndex === -1 ? undefined : currentCart.items[removedIndex];
      const remaining = currentCart.items.filter(
        (it) => it.lineItemId !== lineId,
      );

      // Removing a deal returns its pooled-from-plain choices to standalone
      const restored = removed ? revertPooledItems(removed) : [];

      const cart = cloneCart(currentCart, {
        items: reinsertRevertedLines(remaining, restored, removedIndex),
      });
      dispatch({ type: "SET_CART", cart, locationRef });
    },
    updateItem: (locationRef: string, lineId: string, item: CartItem) => {
      const cart = cloneCart(currentCart, {
        items: currentCart.items.map((it) =>
          it.lineItemId === lineId ? item : it,
        ),
      });
      dispatch({ type: "SET_CART", cart, locationRef });
    },
    selectCartItem: (locationRef: string, cartItem: CartItem) => {
      const inFlightItem = cartItem;
      dispatch({ type: "SELECT_ITEM", inFlightItem, locationRef });
      return inFlightItem.lineItemId;
    },
    // Product
    selectProduct: (
      locationRef: string,
      product: Product,
      source?: ItemSource,
    ) => {
      const inFlightItem = createCartItem(product, {
        isDeal: source === "deal",
      });
      viewItemAnalyticsEvent(
        currency,
        locationRef,
        [inFlightItem],
        getMenus,
        getLocationDeals,
        dispatchOption,
      );
      dispatch({
        type: "SELECT_ITEM",
        inFlightItem,
        locationRef,
        source,
      });

      return inFlightItem.lineItemId;
    },
    updateInFlightCartItem: (locationRef: string, cartItem: CartItem) => {
      dispatch({
        type: "UPDATE_INFLIGHT_CART_ITEM",
        cartItem,
        locationRef,
      });
    },
    removeInFlightCartItem: (locationRef: string) => {
      dispatch({ type: "REMOVE_INFLIGHT_CART_ITEM", locationRef });
    },
    emptyCart: (locationRef: string) => {
      dispatch({ type: "EMPTY_CART", locationRef });
    },
    refreshCartId: (locationRef: string) => {
      const cart = cloneCart(currentCart);
      dispatch({ type: "SET_CART", cart, locationRef });
    },
    reOrderCart: (
      locationRef: string,
      newCart: Cart,
      dispatchOption: OrderDispatchOption,
    ) => {
      const cart = cloneCart(currentCart, newCart);
      addCartItemAnalyticsEvent(
        currency,
        locationRef,
        cart.items,
        getMenus,
        getLocationDeals,
        dispatchOption,
      );
      dispatch({ type: "REORDER_CART", cart, locationRef, dispatchOption });
      submitUpsellForCart(locationRef, cart);
    },
    setCartTags(locationRef: string, tags?: string[]) {
      const cart = cloneCart(currentCart, { tags });
      dispatch({ type: "SET_CART", cart, locationRef });
    },
    setCartVoucherCode(locationRef: string, voucherCode?: string) {
      const cart = cloneCart(currentCart, { voucherCode });

      dispatch({ type: "SET_CART", cart, locationRef });
    },
    setServiceRef(locationRef: string, serviceRef: string) {
      if (dispatchOption !== "SERVICE") return;
      const cart = cloneCart(currentCart);
      dispatch({
        type: "SET_OPTIONS",
        dispatchOption,
        serviceRef,
        cart,
        locationRef,
      });
    },
    setDeliveryAddress(
      locationRef: string,
      deliveryAddress: CheckoutDeliveryAddress,
    ) {
      const cart = cloneCart(currentCart);
      dispatch({
        type: "SET_DELIVERY_ADDRESS",
        deliveryAddress,
        cart,
        locationRef,
      });
    },
    setNotes(locationRef: string, notes: string | null) {
      if (notes === currentCart.notes) return;

      const cart = cloneCart(currentCart, { notes });
      dispatch({
        type: "SET_CART",
        cart,
        locationRef,
      });
    },
    applyServiceCharge(locationRef: string) {
      const cart = cloneCart(currentCart, { serviceChargeRemoved: false });
      dispatch({
        type: "SET_CART",
        cart,
        locationRef,
      });
    },
    removeServiceCharge(locationRef: string) {
      const cart = cloneCart(currentCart, { serviceChargeRemoved: true });
      dispatch({
        type: "SET_CART",
        cart,
        locationRef,
      });
    },
    async assertCartValidity(
      locationRef: string,
      thisCart: Cart,
      user: User | null,
      earliestOrderTime: Date,
      hasUnavailableItems: boolean,
    ) {
      let cart: Cart = thisCart;
      try {
        if (cart.checkingCart) {
          return;
        }

        if (hasUnavailableItems) {
          return;
        }

        const { hash, customer } = getCartCustomer(user, isGuest);

        if (cart.hash !== hash || cartIsStale(cart)) {
          cart = cloneCart(cart);

          if (onCartChangeHook) {
            cart = onCartChangeHook(cart, customer);
          }

          dispatch({
            type: "SET_CART",
            cart,
            locationRef,
          });
          return;
        }

        if (cart.status) return; // already have status of this cart
        if (!cart.items.length) return;
        if (!dispatchOption) return; // should not happen

        if (timeSlot && new Date(timeSlot.startTime) < earliestOrderTime) {
          dispatch({
            type: "CART_BAD",
            locationRef,
            cartId: cart.id,
            reason:
              "Selected time has passed or is too soon. Please choose a new time or ASAP.",
          });
          return;
        }

        const hasIncompleteDeal = cart.items.some((item) => {
          const legacyDeal = legacyDeals.find((d) => d.id === item.productId);
          if (legacyDeal) return !isCartItemDealComplete(item, legacyDeal);

          if (!item.isDeal) return false;

          const dealProduct = resolveDealProduct(item);
          return !isCartItemDealComplete(item, dealProduct);
        });

        if (hasIncompleteDeal) {
          dispatch({
            type: "CART_BAD",
            locationRef,
            cartId: cart.id,
            reason: "Finish your deal before checking out",
          });
          return;
        }

        const exclusiveDealCheck = checkExclusiveDeals(
          cart.items,
          deals,
          legacyDeals,
        );

        if (!exclusiveDealCheck.valid) {
          dispatch({
            type: "CART_BAD",
            locationRef,
            cartId: cart.id,
            reason: exclusiveDealErrorReason(
              exclusiveDealCheck.exclusiveDeals,
              exclusiveDealCheck.nonExclusiveDeals,
            ),
          });
          return;
        }

        dispatch({ type: "CHECKING_CART", locationRef, cart });
        const {
          createCart: { adjustments, discounts, error },
        } = await createCart({
          id: cart.id,
          locationRef,
          fulfillmentType:
            toEnumFulfillmentType(dispatchOption) || FulfillmentType.PickUp,
          items: cart.items.map(toCartItem),
          tags: cart.tags || [],
          deliveryAddress:
            (dispatchOption === "DELIVERY" &&
              toDeliveryAddress(
                deliveryAddress?.address,
                // We only need and send apartment in that mode
                // Ensures we don't send the old apartment from local storage unnecessarily
                apartmentMode ? deliveryAddress?.apartment : undefined,
              )) ||
            undefined,
          serviceRef: (dispatchOption === "SERVICE" && serviceRef) || undefined,
          timeSlot: timeSlot?.ref,
          notes: cart.notes || undefined,
        });

        if (error) {
          dispatch({
            type: "CART_BAD",
            locationRef,
            cartId: cart.id,
            reason: error.message,
          });
          return;
        }

        dispatch({
          type: "CART_GOOD",
          locationRef,
          cartId: cart.id,
          adjustments,
          discounts,
        });
      } catch (error) {
        console.warn("Error creating cart", error);

        const message = parseMutationErrorMessage(error);

        Sentry.addBreadcrumb({
          category: "cart",
          message: "Error creating cart",
          data: { error, message },
        });

        dispatch({
          type: "CART_BAD",
          locationRef,
          cartId: cart.id,
          reason: message,
        });
      }
    },
  };
}

export const CART_MAX_AGE_MINUTES = 1;

function cartIsStale(cart: Cart): boolean {
  if (!cart.updatedAt) return true;

  const maxAge = CART_MAX_AGE_MINUTES * 1000 * 60; // 1 minute in milliseconds

  return Date.now() - new Date(cart.updatedAt).getTime() > maxAge;
}

function cartItemConfigKey(item: CartItem): string {
  const options = item.options
    .map((option) => ({
      optionId: option.optionId,
      choices: option.choices.map(cartItemConfigKey).sort(),
    }))
    .sort((a, b) => a.optionId.localeCompare(b.optionId));

  return JSON.stringify({
    productId: item.productId,
    quantity: item.quantity,
    note: item.note ?? "",
    options,
  });
}

function plainLineMergeKey(item: CartItem): string {
  return cartItemConfigKey({ ...item, quantity: 0 });
}

function revertPooledItems(dealItem: CartItem): CartItem[] {
  if (!dealItem.isDeal) return [];

  const restored: CartItem[] = [];
  const byConfig = new Map<string, CartItem>();

  for (const option of dealItem.options) {
    for (const choice of option.choices) {
      if (!choice.pooledFromPlain) continue;

      const key = plainLineMergeKey(choice);
      const existing = byConfig.get(key);
      if (existing) {
        existing.quantity += choice.quantity;
        continue;
      }

      const plainLine: CartItem = {
        ...choice,
        lineItemId: uuid(),
        isDeal: false,
        pooledFromPlain: undefined,
      };
      byConfig.set(key, plainLine);
      restored.push(plainLine);
    }
  }

  return restored;
}

// Adds a plain item to the cart, folding it into an existing plain line of the
// same config (summing quantity) instead of creating a duplicate line.
function addOrMergePlainItem(items: CartItem[], item: CartItem): CartItem[] {
  const key = plainLineMergeKey(item);
  const match = items.find((it) => !it.isDeal && plainLineMergeKey(it) === key);
  if (!match) return [...items, item];

  return items.map((it) =>
    it === match ? { ...it, quantity: it.quantity + item.quantity } : it,
  );
}

// De-merge placement: puts a deal's restored plain lines back at the deal's slot.
function reinsertRevertedLines(
  items: CartItem[],
  restored: CartItem[],
  dealIndex: number,
): CartItem[] {
  if (restored.length === 0) return items;

  const working = items.map((it) => ({ ...it }));
  const group: CartItem[] = [];
  // Pulling out a line that sat before the deal shifts the deal's slot left.
  let removedBeforeDeal = 0;

  for (const line of restored) {
    const lineKey = plainLineMergeKey(line);
    const idx = working.findIndex(
      (it) => !it.isDeal && plainLineMergeKey(it) === lineKey,
    );
    if (idx === -1) {
      group.push(line);
    } else {
      const [existing] = working.splice(idx, 1);
      if (idx < dealIndex) removedBeforeDeal++;
      group.push({ ...existing, quantity: existing.quantity + line.quantity });
    }
  }

  working.splice(dealIndex - removedBeforeDeal, 0, ...group);
  return working;
}

// TODO: Further enhancement to modify submitUpsellSuggestion to be aware of deals
function stripIsDealForUpsells(
  item: CartItem,
): Omit<CartItem, "isDeal" | "pooledFromPlain"> {
  const { isDeal, pooledFromPlain, options, ...rest } = item;

  return {
    ...rest,
    options: options.map((option) => ({
      ...option,
      choices: option.choices.map(stripIsDealForUpsells),
    })),
  };
}

export interface CartCustomer {
  phone?: string;
  email?: string;
}

function getCartCustomer(
  user: User | null,
  isGuest: boolean,
): { hash: string; customer: CartCustomer } {
  const getData = () => {
    if (isGuest) {
      const guestDetails = getGuestDetailsClient();

      if (guestDetails) {
        return {
          email: guestDetails.emailAddress,
          phone: guestDetails.phoneNumber,
        };
      }
    }
    return {
      email: user?.emailAddress || undefined,
      phone: user?.phoneNumber || undefined,
    };
  };

  const customer = getData();

  const token = getClientAuthToken();

  const hash = crypto
    .createHash("sha256")
    .update(JSON.stringify({ customer, token }))
    .digest("hex");

  return { hash, customer };
}

export function createCartItem(
  product: Product,
  options: {
    initialQuantity?: number;
    isDeal?: boolean;
    // When false, option entries are created but left empty (no default choices).
    // Used for code-entered deals so pooled items fill slots before defaults.
    includeDefaults?: boolean;
  } = {},
): CartItem {
  const {
    initialQuantity = 1,
    isDeal = false,
    includeDefaults = true,
  } = options;

  const item: CartItem = {
    lineItemId: uuid(),
    productId: product.id,
    quantity: initialQuantity,
    options: [],
    isDeal,
  };

  if (product.options) {
    item.options = product.options.map((o) => {
      const option: CartItemOption = {
        optionId: o.id,
        choices: [],
      };

      if (includeDefaults) {
        option.choices = o.choices
          .filter(
            (choice) =>
              (choice.defaultQuantity ?? 0) > 0 && choice.product.available,
          )
          .map((choice) => {
            return createCartItem(choice.product, {
              initialQuantity: choice.defaultQuantity,
            });
          });
      }

      return option;
    });
  }

  return item;
}

// Fills a deal's default choices into options the user left completely empty,
// so pooled/user-provided selections take precedence over the deal's defaults.
export function fillDealDefaults(
  dealItem: CartItem,
  dealProduct: Product,
): CartItem {
  const options = dealItem.options.map((option) => {
    const selected = option.choices.reduce((total, c) => total + c.quantity, 0);
    if (selected > 0) return option; // user/pooled selection wins

    const productOption = dealProduct.options?.find(
      (o) => o.id === option.optionId,
    );
    if (!productOption) return option;

    const defaults = productOption.choices
      .filter(
        (choice) =>
          (choice.defaultQuantity ?? 0) > 0 && choice.product.available,
      )
      .map((choice) =>
        createCartItem(choice.product, {
          initialQuantity: choice.defaultQuantity,
        }),
      );

    return defaults.length ? { ...option, choices: defaults } : option;
  });

  return { ...dealItem, options };
}

type Carts = Map<string, Cart>;

interface CartsService {
  dispatchOption: OrderDispatchOption | null;
  serviceRef: string | null;
  timeSlot: TimeSlot | null;
  carts: Carts;
  deliveryAddress: CheckoutDeliveryAddress | null;
  dispatch: (value: Action) => void;
  onCartChangeHook?: (cart: Cart, cartCustomer: CartCustomer) => Cart;
}

interface CartService {
  cart: Cart;
  dispatchOption: OrderDispatchOption | null;
  deliveryAddress: CheckoutDeliveryAddress | null;
  timeSlot: TimeSlot | null;
  serviceRef: string | null;
  upsellRequest: UpsellRequest | null;
  addItem: (locationRef: string, item: CartItem, list?: string) => void;
  addDealFromCode: (
    locationRef: string,
    dealItem: CartItem,
    dealProduct: Product,
  ) => void;
  removeItem: (locationRef: string, lineId: string) => void;
  updateItem: (locationRef: string, lineId: string, item: CartItem) => void;
  selectCartItem: (locationRef: string, cartItem: CartItem) => string;
  selectProduct: (
    locationRef: string,
    product: Product,
    source?: ItemSource,
  ) => string;
  updateInFlightCartItem: (locationRef: string, cartItem: CartItem) => void;
  removeInFlightCartItem: (locationRef: string) => void;
  emptyCart: (locationRef: string) => void;
  refreshCartId: (locationRef: string) => void;
  reOrderCart: (
    locationRef: string,
    cart: Cart,
    dispatchOption: OrderDispatchOption,
    notes: string,
  ) => void;
  setCartTags: (locationRef: string, tags?: string[]) => void;
  setCartVoucherCode: (locationRef: string, voucherCode?: string) => void;
  setServiceRef: (locationRef: string, serviceRef: string) => void;
  setDeliveryAddress: (
    locationRef: string,
    deliveryAddress: CheckoutDeliveryAddress,
  ) => void;
  setOrderingOptions: (
    dispatchOption: OrderDispatchOption,
    timeSlot: TimeSlot | null,
    serviceRef: string | null,
  ) => void;
  setNotes: (locationRef: string, notes: string | null) => void;
  assertCartValidity: (
    locationRef: string,
    currentCart: Cart,
    user: User | null,
    earliestOrderTime: Date,
    hasUnavailableItems: boolean,
  ) => void;
  applyServiceCharge: (locationRef: string) => void;
  removeServiceCharge: (locationRef: string) => void;
}

const noop: CartsService = {
  carts: new Map<string, Cart>(),
  deliveryAddress: null,
  dispatchOption: null,
  serviceRef: null,
  timeSlot: null,
  dispatch: () => {
    throw new Error("Cart context not setup");
  },
  onCartChangeHook: (cart: Cart) => cart,
};

const cartsCtx = createContext<CartsService>(noop);

type Action =
  | {
      type: "SET_CART";
      locationRef: string;
      cart: Cart;
    }
  | {
      type: "REORDER_CART";
      locationRef: string;
      cart: Cart;
      dispatchOption: OrderDispatchOption;
    }
  | { type: "EMPTY_CART"; locationRef: string }
  | {
      type: "SELECT_ITEM";
      locationRef: string;
      inFlightItem: CartItem;
      source?: ItemSource;
    }
  | {
      type: "UPDATE_INFLIGHT_CART_ITEM";
      locationRef: string;
      cartItem: CartItem;
    }
  | { type: "REMOVE_INFLIGHT_CART_ITEM"; locationRef: string }
  | {
      type: "SET_DELIVERY_ADDRESS";
      deliveryAddress: CheckoutDeliveryAddress;
      cart: Cart;
      locationRef: string;
    }
  | {
      type: "SET_OPTIONS";
      dispatchOption: OrderDispatchOption;
      locationRef: string | null;
      timeSlot?: TimeSlot | null;
      serviceRef?: string | null;
      cart: Cart;
    }
  | { type: "CHECKING_CART"; locationRef: string; cart: Cart }
  | {
      type: "CART_GOOD";
      locationRef: string;
      cartId: string;
      adjustments: CreateCartMutation["createCart"]["adjustments"];
      discounts: CreateCartMutation["createCart"]["discounts"];
    }
  | {
      type: "CART_BAD";
      locationRef: string;
      cartId: string;
      reason: string;
    }
  | {
      type: "UPDATE_CART_UPSELL";
      locationRef: string;
      cartId: string;
      upsellRequest: UpsellRequest;
    };

function updateInFlight(inFlightSegment: CartItem, update: CartItem): CartItem {
  if (inFlightSegment.lineItemId === update.lineItemId) return update;

  // TODO: could use known path to make more efficient
  return {
    ...inFlightSegment,
    options: inFlightSegment.options.map((o) => {
      return {
        ...o,
        choices: o.choices.map((c) => updateInFlight(c, update)),
      };
    }),
  };
}

function reducer(state: State, action: Action): State {
  // copy original state
  const carts = new Map<string, Cart>(state.carts);

  switch (action.type) {
    case "SET_CART": {
      carts.set(action.locationRef, action.cart);
      return { ...state, carts };
    }
    case "REORDER_CART": {
      carts.set(action.locationRef, action.cart);
      return {
        ...state,
        carts,
        dispatchOption: action.dispatchOption,
        timeSlot: null,
        serviceRef: null,
      };
    }
    case "EMPTY_CART": {
      carts.delete(action.locationRef);
      return { ...state, carts };
    }
    case "UPDATE_INFLIGHT_CART_ITEM": {
      const cart = carts.get(action.locationRef);
      if (!cart) throw new Error("No cart to update");
      carts.set(action.locationRef, {
        ...cart,
        inFlightItem:
          cart.inFlightItem &&
          updateInFlight(cart.inFlightItem, action.cartItem),
      });
      return { ...state, carts };
    }
    case "REMOVE_INFLIGHT_CART_ITEM": {
      const cart = carts.get(action.locationRef);
      if (!cart) throw new Error("No cart to update");
      carts.set(action.locationRef, {
        ...cart,
        inFlightItem: undefined,
      });
      return { ...state, carts };
    }
    case "SELECT_ITEM": {
      const cart = carts.get(action.locationRef) || EMPTY_CART;
      carts.set(action.locationRef, {
        ...cart,
        inFlightItem: { ...action.inFlightItem, source: action.source },
      });
      return { ...state, carts };
    }
    case "SET_DELIVERY_ADDRESS": {
      invalidateCarts(carts);
      carts.set(action.locationRef, action.cart);

      return { ...state, deliveryAddress: action.deliveryAddress, carts };
    }
    case "SET_OPTIONS": {
      invalidateCarts(carts);

      return {
        ...state,
        dispatchOption: action.dispatchOption,
        timeSlot: action.timeSlot,
        serviceRef: action.serviceRef,
        carts,
      };
    }
    case "CHECKING_CART": {
      carts.set(action.locationRef, {
        ...action.cart,
        checkingCart: true,
        status: undefined,
        invalidReason: undefined,
        adjustments: undefined,
        discounts: undefined,
      });

      return { ...state, carts };
    }
    case "CART_GOOD": {
      const cart = carts.get(action.locationRef);
      if (!cart) throw new Error("No cart to update");

      if (action.cartId !== cart.id) return state;

      carts.set(action.locationRef, {
        ...cart,
        checkingCart: false,
        status: "valid",
        invalidReason: undefined,
        adjustments: action.adjustments,
        discounts: action.discounts,
      });

      return { ...state, carts };
    }
    case "CART_BAD": {
      const cart = carts.get(action.locationRef);
      if (!cart) throw new Error("No cart to update");

      if (action.cartId !== cart.id) return state;

      carts.set(action.locationRef, {
        ...cart,
        checkingCart: false,
        status: "invalid",
        invalidReason: action.reason,
        adjustments: undefined,
        discounts: undefined,
      });

      return { ...state, carts };
    }
    case "UPDATE_CART_UPSELL": {
      const cart = carts.get(action.locationRef);
      if (!cart) throw new Error("No cart to update");

      if (action.cartId !== cart.id) return state;

      carts.set(action.locationRef, {
        ...cart,
        upsellRequest: action.upsellRequest,
      });

      return { ...state, carts };
    }
  }
}

function invalidateCarts(
  carts: Map<string, Cart>,
  locationRef?: string | null,
) {
  carts.forEach((cart, key, map) => {
    // don't invalidate the current location's cart if ref provided
    if (locationRef === key) return;

    map.set(key, {
      ...cart,
      id: ulid(),
      status: undefined,
      checkingCart: undefined,
      invalidReason: undefined,
    });
  });
}

export function mapCartRecordToCart(cart: CartRecord): Cart {
  return {
    id: ulid(),
    items: cart.items.map((item) => mapCartItemRecordToCartItem(item)),
    notes: undefined,
    updatedAt: new Date(),
  };
}

function mapCartItemRecordToCartItem(item: CartItemRecord): CartItem {
  {
    const cartItem: CartItem = {
      lineItemId: uuid(),
      productId: item.id,
      quantity: item.quantity,
      note: item.note || undefined,
      options: item.options
        ? item.options.map((o) => mapCartItemOptionRecordToCartItemOption(o))
        : [],
    };

    return cartItem;
  }
}

function mapCartItemOptionRecordToCartItemOption(
  optionRecord: CartItemOptionRecord,
): CartItemOption {
  const option: CartItemOption = {
    optionId: optionRecord.id,
    choices: optionRecord.choices.map((c) =>
      mapCartItemChoiceRecordToCartItemChoice(c),
    ),
  };
  return option;
}

function mapCartItemChoiceRecordToCartItemChoice(
  choice: CartItemChoiceRecord,
): CartItem {
  const cartItem: CartItem = {
    lineItemId: uuid(),
    productId: choice.id,
    quantity: choice.quantity,
    options: choice.options
      ? choice.options.map((o) => mapCartItemOptionRecordToCartItemOption(o))
      : [],
    note: undefined,
  };
  return cartItem;
}

function toCartItem(input: CartItem): CartItemInput {
  return {
    id: input.productId,
    quantity: input.quantity,
    note: input.note,
    options: input.options.map(toOption),
    itemType: input.isDeal ? CartItemType.Deal : CartItemType.Product,
  };
}

function toOption(input: CartItemOption): CartItemOptionInput {
  return {
    id: input.optionId,
    choices: input.choices.map(toChoice),
  };
}

function toChoice(input: CartItem): CartItemChoiceInput {
  return {
    id: input.productId,
    quantity: input.quantity,
    options: input.options.map(toOption),
  };
}
